import { apiClient } from '../client';

/**
 * Endpoints relacionados ao sistema de moeda da escola (wallet/PDV)
 */

/**
 * Obtém saldo do estudante
 * @param {string} studentCode - Código do estudante
 * @returns {Promise<{studentCode: string, balance: number}>}
 */
const getBalance = async (studentCode) => {
  const { data } = await apiClient.get(`/students/${studentCode}/balance`);
  return data;
};

/**
 * Obtém informações do cartão (estudante ou colaborador)
 * @param {string} cardCode - Código do cartão (pode ser studentCode ou employeeCode)
 * @returns {Promise<{cardCode: string, ownerType: 'STUDENT'|'EMPLOYEE', name: string, balance: number|null}>}
 */
const getCardInfo = async (cardCode) => {
  const { data } = await apiClient.get(`/canteen/cards/${cardCode}/info`);
  return data;
};

/**
 * Obtém extrato do estudante
 * @param {string} studentCode - Código do estudante
 * @param {Object} options - Opções (limit, offset)
 * @returns {Promise<{studentCode: string, entries: Array, limit: number, offset: number}>}
 */
const getStatement = async (studentCode, options = {}) => {
  const { limit = 50, offset = 0 } = options;
  const { data } = await apiClient.get(`/students/${studentCode}/statement`, {
    params: { limit, offset },
  });
  return data;
};

/**
 * Lista produtos ativos (para PDV)
 * @param {string} branch - Filial ('ALTO_BRANCO' ou 'CONCEICAO')
 * @returns {Promise<{products: Array}>}
 */
const getProducts = async (branch = null) => {
  const params = {};
  if (branch) {
    params.branch = branch;
  }
  const { data } = await apiClient.get('/canteen/products', { params });
  return data;
};

/**
 * Lista todos os produtos (ativos e inativos) - ADMIN
 * @returns {Promise<{products: Array}>}
 */
const getAllProducts = async () => {
  const { data } = await apiClient.get('/admin/products');
  return data;
};

/**
 * Cria produto (ADMIN ou CANTEEN)
 * @param {Object} productData - Dados do produto
 * @returns {Promise<{product: Object}>}
 */
const createProduct = async (productData) => {
  const { data } = await apiClient.post('/canteen/products', productData);
  return data;
};

/**
 * Atualiza produto (ADMIN)
 * @param {number} productId - ID do produto
 * @param {Object} productData - Dados do produto para atualizar
 * @returns {Promise<{product: Object}>}
 */
const updateProduct = async (productId, productData) => {
  const { data } = await apiClient.put(`/admin/products/${productId}`, productData);
  return data;
};

/**
 * Deleta produto (ADMIN)
 * @param {number} productId - ID do produto
 * @returns {Promise<{message: string, productId: number}>}
 */
const deleteProduct = async (productId) => {
  const { data } = await apiClient.delete(`/admin/products/${productId}`);
  return data;
};

/**
 * Cria pedido (venda)
 * @param {Object} orderData - Dados do pedido
 * @param {string} orderData.cardCode - Código do cartão (pode ser studentCode ou employeeCode)
 * @param {string} orderData.studentCode - Código do estudante (compatibilidade, use cardCode)
 * @param {Array} orderData.items - Array de {productId, qty}
 * @param {string} orderData.branch - Filial ('ALTO_BRANCO' ou 'CONCEICAO')
 * @returns {Promise<{orderId: number, totalCoins: number, balanceAfter: number|null, ownerType: 'STUDENT'|'EMPLOYEE'}>}
 */
const createOrder = async (orderData) => {
  // Tentar recuperar do localStorage se não estiver no orderData
  if (!orderData.branch) {
    try {
      const savedBranch = localStorage.getItem('canteen_filial');
      if (savedBranch && (savedBranch === 'ALTO_BRANCO' || savedBranch === 'CONCEICAO')) {
        orderData.branch = savedBranch;
      }
    } catch (error) {
      console.error('[wallet.js] Erro ao ler localStorage:', error);
    }
  }

  // Validar que filial está presente e é válida - OBRIGATÓRIA
  if (!orderData.branch) {
    const error = new Error('Filial é obrigatória. Selecione uma filial antes de finalizar a venda.');
    console.error('[wallet.js] Erro - Filial não encontrada:', {
      orderDataKeys: Object.keys(orderData),
      branchValue: orderData.branch,
      branchType: typeof orderData.branch,
    });
    throw error;
  }
  
  if (orderData.branch !== 'ALTO_BRANCO' && orderData.branch !== 'CONCEICAO') {
    const error = new Error('Filial inválida. Deve ser ALTO_BRANCO ou CONCEICAO. Recebido: ' + orderData.branch);
    console.error('[wallet.js] Erro - Filial inválida:', orderData.branch);
    throw error;
  }

  const { data } = await apiClient.post('/canteen/orders', orderData);
  return data;
};

/**
 * Obtém pedido por ID
 * @param {number} orderId - ID do pedido
 * @returns {Promise<{order: Object}>}
 */
const getOrder = async (orderId) => {
  const { data } = await apiClient.get(`/canteen/orders/${orderId}`);
  return data;
};

/**
 * Cria estudante (ADMIN)
 * @param {Object} studentData - Dados do estudante
 * @param {string} studentData.name - Nome do estudante
 * @param {string} studentData.studentCode - Código único do estudante
 * @param {boolean} studentData.isActive - Se está ativo
 * @returns {Promise<{student: Object}>}
 */
const createStudent = async (studentData) => {
  const { data } = await apiClient.post('/admin/students', studentData);
  return data;
};

/**
 * Lista estudantes cadastrados no sistema de moeda da escola (ADMIN)
 * Inclui nome, código e saldo atual.
 * @returns {Promise<{students: Array}>}
 */
const getWalletStudents = async () => {
  const { data } = await apiClient.get('/admin/students');
  return data;
};

/**
 * Lista estudantes com NFC (ativos e inativos) com paginação e filtros.
 * @param {Object} params
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=20]
 * @param {string} [params.search] - Filtro por nome
 * @param {'all'|'active'|'inactive'} [params.status='all']
 * @returns {Promise<{students: Array, pagination: {page:number,pageSize:number,total:number,totalPages:number}}>}
 */
const getWalletStudentsPaginated = async (params = {}) => {
  const { data } = await apiClient.get('/admin/wallet-students', {
    params,
  });
  return data;
};

/**
 * Atualiza dados de um estudante (ADMIN)
 * @param {number} studentId - ID do estudante
 * @param {Object} updateData - Campos para atualizar (studentCode, isActive, name, etc.)
 * @returns {Promise<{student: Object}>}
 */
const updateWalletStudent = async (studentId, updateData) => {
  const { data } = await apiClient.put(`/admin/students/${studentId}`, updateData);
  return data;
};

/**
 * Cria parent (ADMIN)
 * @param {Object} parentData - Dados do parent
 * @param {string} parentData.name - Nome do parent
 * @param {string} parentData.email - Email do parent
 * @param {string} parentData.password - Senha do parent
 * @returns {Promise<{user: Object}>}
 */
const createParent = async (parentData) => {
  const { data } = await apiClient.post('/admin/parents', parentData);
  return data;
};

/**
 * Vincula parent a student (ADMIN)
 * @param {Object} linkData - Dados do vínculo
 * @param {number} linkData.parentId - ID do parent
 * @param {number} linkData.studentId - ID do student
 * @returns {Promise<{link: Object}>}
 */
const linkParentStudent = async (linkData) => {
  const { data } = await apiClient.post('/admin/link-parent-student', linkData);
  return data;
};

/**
 * Cria recarga manual (ADMIN)
 * @param {Object} topupData - Dados da recarga
 * @param {string} topupData.studentCode - Código do estudante
 * @param {number} topupData.amountCoins - Quantidade de coins
 * @param {string} topupData.note - Observação opcional
 * @returns {Promise<{ledgerEntry: Object, balance: number}>}
 */
const createManualTopup = async (topupData) => {
  const { data } = await apiClient.post('/admin/topups/manual', topupData);
  return data;
};

/**
 * Fechamento de caixa - relatório de vendas por período (ADMIN)
 * @param {string} startDate - Data início (YYYY-MM-DD)
 * @param {string} endDate - Data fim (YYYY-MM-DD)
 * @param {string} branch - Filial ('ALTO_BRANCO' ou 'CONCEICAO'), opcional
 * @returns {Promise<{period: Object, summary: Object, orders: Array, itemsSold: Array}>}
 */
const getCashClosing = async (startDate, endDate, branch = null) => {
  const params = { startDate, endDate };
  if (branch) {
    params.branch = branch;
  }
  const { data } = await apiClient.get('/admin/cash-closing', { params });
  return data;
};

/**
 * Cria entrada de estoque (ADMIN)
 * @param {Object} entryData - Dados da entrada
 * @param {number} entryData.productId - ID do produto
 * @param {number} entryData.quantity - Quantidade
 * @param {string} entryData.entryDate - Data da entrada (YYYY-MM-DD)
 * @param {string} entryData.note - Observação opcional
 * @returns {Promise<{entry: Object}>}
 */
const createStockEntry = async (entryData) => {
  const { data } = await apiClient.post('/admin/stock/entries', entryData);
  return data;
};

/**
 * Lista entradas de estoque (ADMIN)
 * @param {Object} filters - Filtros opcionais
 * @param {number} filters.productId - ID do produto
 * @param {string} filters.startDate - Data início (YYYY-MM-DD)
 * @param {string} filters.endDate - Data fim (YYYY-MM-DD)
 * @param {string} filters.branch - Filial ('ALTO_BRANCO' ou 'CONCEICAO')
 * @returns {Promise<{entries: Array}>}
 */
const getStockEntries = async (filters = {}) => {
  const { data } = await apiClient.get('/admin/stock/entries', {
    params: filters,
  });
  return data;
};

/**
 * Relatório de estoque agrupado por produto (ADMIN)
 * @param {Object} filters - Filtros opcionais
 * @param {string} filters.startDate - Data início (YYYY-MM-DD)
 * @param {string} filters.endDate - Data fim (YYYY-MM-DD)
 * @param {string} filters.branch - Filial ('ALTO_BRANCO' ou 'CONCEICAO')
 * @returns {Promise<{period: Object, report: Array}>}
 */
const getStockReport = async (filters = {}) => {
  const { data } = await apiClient.get('/admin/stock/report', {
    params: filters,
  });
  return data;
};

/**
 * Cria colaborador (ADMIN)
 * @param {Object} employeeData - Dados do colaborador
 * @param {string} employeeData.name - Nome do colaborador
 * @param {string} employeeData.employeeCode - Código único do colaborador
 * @param {boolean} employeeData.isActive - Se está ativo
 * @returns {Promise<{employee: Object}>}
 */
const createEmployee = async (employeeData) => {
  const { data } = await apiClient.post('/admin/employees', employeeData);
  return data;
};

/**
 * Lista colaboradores cadastrados no sistema de moeda da escola (ADMIN)
 * @returns {Promise<{employees: Array}>}
 */
const getWalletEmployees = async () => {
  const { data } = await apiClient.get('/admin/employees');
  return data;
};

/**
 * Lista colaboradores com NFC (ativos e inativos) com paginação e filtros.
 * @param {Object} params
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=20]
 * @param {string} [params.search] - Filtro por nome
 * @param {'all'|'active'|'inactive'} [params.status='all']
 * @returns {Promise<{employees: Array, pagination: {page:number,pageSize:number,total:number,totalPages:number}}>}
 */
const getWalletEmployeesPaginated = async (params = {}) => {
  const { data } = await apiClient.get('/admin/wallet-employees', {
    params,
  });
  return data;
};

/**
 * Atualiza dados de um colaborador (ADMIN)
 * @param {number} employeeId - ID do colaborador
 * @param {Object} updateData - Campos para atualizar (employeeCode, isActive, name, cpf, etc.)
 * @returns {Promise<{employee: Object}>}
 */
const updateWalletEmployee = async (employeeId, updateData) => {
  const { data } = await apiClient.put(`/admin/employees/${employeeId}`, updateData);
  return data;
};

/**
 * Relatório de compras por colaborador por período (ADMIN)
 * @param {number} employeeId - ID do colaborador
 * @param {Object} params
 * @param {string} params.startDate - Data inicial (YYYY-MM-DD)
 * @param {string} params.endDate - Data final (YYYY-MM-DD)
 * @returns {Promise<{employee: Object, period: Object, summary: Object, orders: Array}>}
 */
const getEmployeeMonthlyReport = async (employeeId, params) => {
  const { data } = await apiClient.get(`/admin/employees/${employeeId}/monthly-report`, {
    params,
  });
  return data;
};

export {
  getBalance,
  getCardInfo,
  getStatement,
  getProducts,
  getAllProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  createOrder,
  getOrder,
  createStudent,
  getWalletStudents,
  getWalletStudentsPaginated,
  updateWalletStudent,
  createParent,
  linkParentStudent,
  createManualTopup,
  getCashClosing,
  createStockEntry,
  getStockEntries,
  getStockReport,
  createEmployee,
  getWalletEmployees,
  getWalletEmployeesPaginated,
  updateWalletEmployee,
  getEmployeeMonthlyReport,
};
